import React from "react";
import { motion } from "framer-motion";
import { ScanSearch, BarChart2, FileText, Lock } from "lucide-react";

const capabilities = [
  {
    icon: ScanSearch,
    title: "Graft Count Estimation",
    description:
      "Computer vision analysis identifies and counts individual grafts in post-operation scalp images.",
  },
  {
    icon: BarChart2,
    title: "Density Mapping",
    description:
      "Visualizes implantation distribution across the scalp, highlighting high and low-density zones.",
  },
  {
    icon: FileText,
    title: "Verification Reports",
    description:
      "Generates structured, downloadable PDF reports documenting analysis results with patient details.",
  },
  {
    icon: Lock,
    title: "Privacy by Design",
    description:
      "Images are processed in real time and never retained. No patient data is stored on our servers.",
  },
];

const ProductOverview = () => {
  return (
    <section id="product" className="relative py-24 sm:py-32 bg-[#F7F9FC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl mb-16 sm:mb-20"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-xs font-medium tracking-[0.1em] uppercase mb-6">
            What It Does
          </div>
          <h2
            className="text-slate-900 font-semibold tracking-tight leading-tight mb-4"
            style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)" }}
          >
            Objective analysis of hair transplant outcomes
          </h2>
          <p className="text-slate-500 text-lg leading-relaxed">
            Graft Counter brings technology to a field where verification has
            historically relied on subjective estimation. Four core capabilities
            — in a single workflow.
          </p>
        </motion.div>

        {/* Capabilities grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {capabilities.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="bg-white rounded-2xl p-6 border border-slate-200/80 hover:border-blue-200 hover:shadow-md transition-all duration-300 group"
            >
              <div className="w-10 h-10 bg-blue-50 group-hover:bg-blue-100 rounded-xl flex items-center justify-center mb-5 transition-colors duration-300">
                <item.icon className="w-5 h-5 text-blue-600 stroke-[1.5]" />
              </div>
              <h3 className="text-slate-900 font-semibold text-base mb-2 leading-snug">
                {item.title}
              </h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                {item.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductOverview;
